'use client';

import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { FileText, Loader2, Trash2 } from 'lucide-react';

// Deep-import the concrete module, not the `@naumu/shared` barrel - the barrel
// re-exports every schemas/*.ts and drags zod (~299 KiB) into any chunk that
// touches it, including the marketing shell's.
import { isAgentReadable } from '@naumu/shared/types/attachment';

import { AttachmentLightbox } from '../attachment-lightbox';
import { Button } from '../button';
import { Tooltip } from '../tooltip';
import { AgentNotReadableBadge } from '../agent-not-readable-badge';
import { useDismiss } from '../use-dismiss';
import { useStagedRemoval } from '../use-staged-removal';
import { cn } from '../../utils/cn';

import type { StagedAttachment } from './staged-attachment';
import { getStagedLightboxAttachments } from './staged-attachment-lightbox';

interface ComposerAttachmentStackProps {
	attachments: StagedAttachment[];
	onRemove: (id: string) => void;
	onExpand?: () => void;
	className?: string;
}

const MotionButton = motion.create(Button);

// Deterministic rotation per attachment so the stack doesn't reshuffle on re-render.
const hashAngle = (id: string) => {
	let h = 0;
	for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
	return ((Math.abs(h) % 181) - 90) / 10;
};

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Thumbnail = ({ att, size = 56 }: { att: StagedAttachment; size?: number }) => {
	const base =
		'aspect-square shrink-0 rounded-[8px] border border-solid border-border shadow-[0px_0px_16px_0px_rgba(0,0,0,0.25)] overflow-hidden';
	const sizeStyle = { width: size, height: size } as const;
	const iconSize = Math.round(size * 0.36);
	// Any captured poster (a video's still frame or a PDF's page-1 render)
	// renders as an image — matches what the embedded message will show.
	if (att.thumbnailUrl) {
		return (
			<img
				src={att.thumbnailUrl}
				alt={att.fileName}
				style={sizeStyle}
				className={cn(base, 'object-cover object-top')}
			/>
		);
	}
	if (att.fileType.startsWith('video/') && att.previewUrl) {
		// Live <video> frame while/if the poster isn't ready.
		return (
			<video
				src={att.previewUrl}
				style={sizeStyle}
				className={cn(base, 'object-cover')}
				muted
				preload="metadata"
			/>
		);
	}
	if (att.previewUrl && att.fileType.startsWith('image/')) {
		return (
			<img
				src={att.previewUrl}
				alt={att.fileName}
				style={sizeStyle}
				className={cn(base, 'object-cover')}
			/>
		);
	}
	return (
		<div
			style={sizeStyle}
			className={cn(base, 'flex items-center justify-center bg-overlay')}
		>
			<FileText style={{ width: iconSize, height: iconSize }} className="text-neutral/50" strokeWidth={2} />
		</div>
	);
};

const STACK_Y = -8;
const ROW_HEIGHT = 48;
const MAX_SURFACE_HEIGHT = 420;
const STACK_W = 40;

const ExpandedAttachmentRow = ({
	att,
	onRemove,
	onPreview,
}: {
	att: StagedAttachment;
	onRemove: () => void;
	/** Absent when the file has nothing the lightbox can render (zip, docx, …). */
	onPreview?: () => void;
}) => {
	const { isRemoving, requestRemoval } = useStagedRemoval(onRemove);
	// The row already contains the remove Button, so the clickable surface can't
	// be a <button> (no nested buttons) — it takes the explicit ARIA treatment.
	const previewProps = onPreview
		? {
				role: 'button',
				tabIndex: 0,
				'aria-label': `Preview ${att.fileName}`,
				onClick: onPreview,
				onKeyDown: (e: ReactKeyboardEvent) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onPreview();
					}
				},
			}
		: {};
	return (
		<li
			className="removing-item-vertical"
			data-removing={isRemoving ? 'true' : undefined}
		>
			<div
				className={cn(
					'flex items-center gap-[8px] rounded-[8px]',
					onPreview &&
						'cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border',
				)}
				{...previewProps}
			>
				<motion.div
					layoutId={`thumb-${att.id}`}
					style={{ rotate: 0, transformOrigin: 'center' }}
					transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
				>
					<Thumbnail att={att} size={32} />
				</motion.div>
				<motion.div
					className="flex min-w-0 flex-col"
					style={{ width: 160 }}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2, delay: 0.08 }}
				>
					<div className="flex items-center gap-1">
						<span className="truncate text-[12px] font-normal leading-[16px] text-neutral">
							{att.fileName}
						</span>
						{!isAgentReadable(att.fileType, att.fileName) && (
							<AgentNotReadableBadge size={14} className="shrink-0" />
						)}
					</div>
					<span className="truncate text-[12px] font-bold leading-[16px] text-neutral-weak">
						{att.status === 'uploading' ? (
							<span className="inline-flex items-center gap-1">
								<Loader2 className="size-[12px] animate-spin" />
								{att.progress}%
							</span>
						) : att.status === 'error' ? (
							<span className="text-danger">Failed</span>
						) : (
							formatFileSize(att.fileSize)
						)}
					</span>
				</motion.div>
				<Tooltip content={`Remove ${att.fileName}`}>
					<MotionButton
						variant="ghost"
						size="sm"
						iconOnly
						onClick={(e) => {
							e.stopPropagation();
							requestRemoval();
						}}
						aria-label={`Remove ${att.fileName}`}
						initial={{ opacity: 0 }}
						animate={{ opacity: 0.4 }}
						exit={{ opacity: 0 }}
						whileHover={{ opacity: 1 }}
						transition={{ duration: 0.2, delay: 0.1 }}
					>
						<Trash2 strokeWidth={2} />
					</MotionButton>
				</Tooltip>
			</div>
		</li>
	);
};

// mirrored-by: @marketing/modules/marketing/components/fixtures/fixture-memory-import-cells.tsx (FixtureAttachmentStack) - propagate visual changes to the collapsed stack AND the expanded popover
export const ComposerAttachmentStack = ({
	attachments,
	onRemove,
	onExpand,
	className,
}: ComposerAttachmentStackProps) => {
	const [expanded, setExpanded] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const closeTimerRef = useRef<number | null>(null);
	const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
	// Pre-send preview: index into `previewable`, not into `attachments`.
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

	// Tap-outside / Escape closes — covers mobile taps (where hover doesn't apply)
	// and also gives desktop a clean dismissal when the pointer never entered.
	useDismiss(expanded, () => setExpanded(false), [wrapperRef, popoverRef]);

	useEffect(() => {
		if (!expanded || !wrapperRef.current) return;
		const update = () => {
			if (wrapperRef.current) {
				setAnchorRect(wrapperRef.current.getBoundingClientRect());
			}
		};
		update();
		window.addEventListener('scroll', update, true);
		window.addEventListener('resize', update);
		return () => {
			window.removeEventListener('scroll', update, true);
			window.removeEventListener('resize', update);
		};
	}, [expanded]);

	useEffect(() => {
		return () => {
			if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
		};
	}, []);

	if (attachments.length === 0) return null;

	const previewable = getStagedLightboxAttachments(attachments);

	const clearCloseTimer = () => {
		if (closeTimerRef.current) {
			window.clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	};

	// Grace period so the pointer can cross the gap between the stack (inside
	// ComposerShell) and the portaled popover (in document.body) without the
	// popover collapsing mid-traverse.
	const scheduleClose = () => {
		clearCloseTimer();
		closeTimerRef.current = window.setTimeout(() => setExpanded(false), 80);
	};

	const open = () => {
		clearCloseTimer();
		if (!expanded) {
			if (wrapperRef.current) {
				setAnchorRect(wrapperRef.current.getBoundingClientRect());
			}
			setExpanded(true);
			onExpand?.();
		}
	};

	const handleToggle = () => {
		clearCloseTimer();
		if (expanded) {
			setExpanded(false);
		} else {
			open();
		}
	};

	// Opening the preview retires the popover — the lightbox is a full-screen
	// surface, and leaving the list hovering under it reads as two open menus.
	const openPreview = (id: string) => {
		const index = previewable.findIndex((p) => p.id === id);
		if (index === -1) return;
		clearCloseTimer();
		setExpanded(false);
		setLightboxIndex(index);
	};

	return (
		<LayoutGroup>
			<div
				ref={wrapperRef}
				className={cn('relative z-20 h-[32px] shrink-0 cursor-pointer', className)}
				style={{ width: STACK_W }}
				onPointerEnter={(e) => {
					if (e.pointerType === 'mouse') open();
				}}
				onPointerLeave={(e) => {
					if (e.pointerType === 'mouse') scheduleClose();
				}}
				onClick={handleToggle}
				role="button"
				tabIndex={0}
				aria-label={expanded ? 'Hide attachments' : 'Show attachments'}
				aria-expanded={expanded}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handleToggle();
					}
				}}
			>
				{!expanded && (
					<div className="pointer-events-none absolute inset-0">
						{attachments.map((att, i) => {
							const angle = hashAngle(att.id);
							const stackedTop = STACK_Y + i * 2;
							const stackedLeft = STACK_W - 2 - ROW_HEIGHT;
							const isTopmost = i === attachments.length - 1;
							const showCountOverlay = isTopmost && attachments.length > 1;
							return (
								<motion.div
									key={att.id}
									layoutId={`thumb-${att.id}`}
									className="absolute"
									style={{
										top: stackedTop,
										left: stackedLeft,
										zIndex: i + 1,
										rotate: angle,
										transformOrigin: 'center',
									}}
									transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
								>
									<div className="relative">
										<Thumbnail att={att} size={ROW_HEIGHT} />
										{showCountOverlay && (
											<div
												className="pointer-events-none absolute inset-0 rounded-[8px] bg-surface/50"
												aria-hidden
											/>
										)}
									</div>
								</motion.div>
							);
						})}
						{attachments.length > 1 && (
							<div
								className="pointer-events-none absolute flex items-center justify-center"
								style={{
									top: STACK_Y + (attachments.length - 1) * 2,
									left: STACK_W - 2 - ROW_HEIGHT,
									width: ROW_HEIGHT,
									height: ROW_HEIGHT,
									zIndex: attachments.length + 1,
								}}
								aria-hidden
							>
								<span className="text-[16px] font-bold leading-none text-neutral [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]">
									{attachments.length}
								</span>
							</div>
						)}
					</div>
				)}

			</div>
			{typeof document !== 'undefined' &&
				createPortal(
					<AnimatePresence>
						{expanded && anchorRect && (
							<motion.div
								ref={popoverRef}
								// Portaled to document.body so backdrop-filter operates against
								// the page backdrop instead of ComposerShell's already-filtered
								// layer (nested backdrop-filter renders as a no-op).
								className="fixed z-[9999] rounded-[20px] border border-solid border-border-weak bg-overlay backdrop-blur-[24px]"
								style={{
									bottom: window.innerHeight - anchorRect.top - 20,
									left: anchorRect.left - 24,
									maxHeight: MAX_SURFACE_HEIGHT,
									pointerEvents: 'auto',
									WebkitBackdropFilter: 'blur(24px)',
								}}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
								onPointerEnter={(e) => {
									if (e.pointerType === 'mouse') clearCloseTimer();
								}}
								onPointerLeave={(e) => {
									if (e.pointerType === 'mouse') scheduleClose();
								}}
							>
								<ul
									className="flex flex-col-reverse gap-[6px] overflow-y-auto p-[12px]"
									style={{ maxHeight: MAX_SURFACE_HEIGHT }}
								>
									{attachments.map((att) => (
										<ExpandedAttachmentRow
											key={att.id}
											att={att}
											onRemove={() => onRemove(att.id)}
											onPreview={
												previewable.some((p) => p.id === att.id)
													? () => openPreview(att.id)
													: undefined
											}
										/>
									))}
								</ul>
							</motion.div>
						)}
					</AnimatePresence>,
					document.body
				)}

			{/* Pre-send preview. The index is re-checked against the live list so
			    removing the open file (or one before it) can't strand the carousel
			    on a revoked object URL. */}
			{lightboxIndex !== null && lightboxIndex < previewable.length && (
				<AttachmentLightbox
					attachments={previewable}
					initialIndex={lightboxIndex}
					onClose={() => setLightboxIndex(null)}
				/>
			)}
		</LayoutGroup>
	);
};
